/**
 * "New tenant" (vendor.admin, D13, D39): its id (chosen once, a key segment), its name, its first admin's email —
 * the tenant is created with its default groups and that admin, who is emailed ACP's invite — and the staff's record
 * of it: a customer or a demo tenant (a staff member running a demo is its first admin, with a second, ordinary
 * account), a contact and internal notes the tenant never sees.
 */
import { emailWireSchema } from '@norna/api-contract/tenancy.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Textarea } from '../ui/textarea.js';
import { tenantIdWireSchema } from '@norna/api-contract/primitives.js';

export type NewTenant = {
  readonly tenantId: string;
  readonly name: string;
  readonly adminEmail: string;
  readonly kind: 'customer' | 'demo';
  readonly contact: string;
  readonly notes: string;
};

export type CreateTenantDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (tenant: NewTenant) => void;
};

function CreateTenantForm({ onOpenChange, pending, error, onSubmit }: Omit<CreateTenantDialogProps, 'open'>) {
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [kind, setKind] = useState<'customer' | 'demo'>('customer');
  const [contact, setContact] = useState('');
  const [notes, setNotes] = useState('');
  const idError = tenantId === '' || tenantIdWireSchema.safeParse(tenantId.trim()).success ? null : '3–40 lowercase letters, digits or hyphens, starting with a letter.';
  const emailError = adminEmail === '' || emailWireSchema.safeParse(adminEmail.trim()).success ? null : 'Not an email address.';
  const ready = tenantId.trim() !== '' && name.trim() !== '' && adminEmail.trim() !== '' && idError === null && emailError === null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({ tenantId: tenantId.trim(), name: name.trim(), adminEmail: adminEmail.trim(), kind, contact: contact.trim(), notes });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New tenant</DialogTitle>
        <DialogDescription>Creates the tenant with its default groups (Admin, Producer/Editor, Operator, Viewer) and its first admin, who is emailed an invite.</DialogDescription>
      </DialogHeader>
      <Field invalid={idError !== null}>
        <Label htmlFor="tenant-id">Tenant id</Label>
        <Input id="tenant-id" value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={pending} aria-invalid={idError !== null} placeholder="acme-tv" autoFocus />
        <FieldDescription>Permanent: it is part of every key and every node’s DNS name.</FieldDescription>
        <FieldError>{idError}</FieldError>
      </Field>
      <Field>
        <Label htmlFor="tenant-name">Name</Label>
        <Input id="tenant-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} />
      </Field>
      <Field invalid={emailError !== null}>
        <Label htmlFor="tenant-admin">First admin’s email</Label>
        <Input id="tenant-admin" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} disabled={pending} aria-invalid={emailError !== null} />
        <FieldError>{emailError}</FieldError>
      </Field>
      <Field>
        <Label htmlFor="tenant-kind">Kind</Label>
        <NativeSelect id="tenant-kind" value={kind} onChange={(e) => setKind(e.target.value === 'demo' ? 'demo' : 'customer')} disabled={pending}>
          <option value="customer">Customer</option>
          <option value="demo">Demo</option>
        </NativeSelect>
        <FieldDescription>A demo tenant is for showing the product: invite your own ordinary account as its admin.</FieldDescription>
      </Field>
      <Field>
        <Label htmlFor="tenant-contact">Contact</Label>
        <Input id="tenant-contact" value={contact} onChange={(e) => setContact(e.target.value)} disabled={pending} maxLength={500} />
      </Field>
      <Field>
        <Label htmlFor="tenant-notes">Internal notes</Label>
        <Textarea id="tenant-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={pending} maxLength={5000} rows={3} />
        <FieldDescription>Staff only — never shown to the tenant.</FieldDescription>
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-tenant-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateTenantDialog({ open, ...rest }: CreateTenantDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-tenant-dialog">
        <CreateTenantForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
